import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSession, useUser } from '@clerk/clerk-react';
import { Buildings, Cube, User, UserCircleGear, Wrench, CaretDown, Plus, X } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import { Checkbox } from 'components/ui/checkbox';
import { EnvelopeSimple, Warning } from '@phosphor-icons/react';
import { RenoRecipientSearch, AddedRecipient } from './RenoRecipientSearch';
import { RenoAddEmailInline } from './RenoAddEmailInline';
import { RenoMailInfoNotice } from './RenoMailInfoNotice';
import { renoMailBlockReason } from '../utils/renoMailBlockReason';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useBuildingsAndUnits } from 'hooks/useBuildingsAndUnits';
import { useActiveUsers } from 'shared/contexts/users.context';
import { getUserName } from 'shared/utils/users';
import { UniversalPickerModal } from 'shared/components/UniversalPickerModal';
import {
  RenoTicketPriority,
  RenoTicketSource,
  RenoMessageType,
} from '../types/renoTicket';

export interface RenoMailIntent {
  channel: 'supplier' | 'tenant';
  recipients: { name: string; email: string }[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // mailIntent aanwezig => na het aanmaken moet er een mail opgesteld/verstuurd worden.
  onCreated: (data: any, mailIntent?: RenoMailIntent) => void | Promise<any>;
}

interface RenovationProject {
  id: number;
  title: string;
}

interface SupplierAssignment {
  supplier?: { id: number; name: string };
}

interface ProjectPart {
  id: number;
  title: string;
  label?: { id: number; name: string } | null;
  supplierAssignments?: SupplierAssignment[];
}

const selectContentProps = {
  position: 'popper' as const,
  sideOffset: 4,
  align: 'start' as const,
  className: 'max-h-[220px]',
  modal: false,
};

// Compacte zoek-en-selecteer-knop (gebouw/project) zonder geneste dialogen.
function InlineSearchSelect({
  label,
  icon,
  placeholder,
  selectedLabel,
  options,
  isLoading,
  onSelect,
}: {
  label: string;
  icon: React.ReactNode;
  placeholder: string;
  selectedLabel?: string | null;
  options: { id: string; label: string; sub?: string }[];
  isLoading?: boolean;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = useMemo(
    () =>
      search
        ? options.filter((o) => `${o.label} ${o.sub ?? ''}`.toLowerCase().includes(search.toLowerCase()))
        : options,
    [options, search],
  );

  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-[var(--border)] px-3 py-2 text-sm text-left hover:bg-[var(--muted)] transition-colors"
      >
        <span className={`flex items-center gap-2 truncate ${selectedLabel ? '' : 'text-[var(--muted-foreground)]'}`}>
          {icon}
          {selectedLabel || placeholder}
        </span>
        <CaretDown size={14} className="shrink-0 text-[var(--muted-foreground)]" />
      </button>
      {open && (
        <div className="rounded-md border border-[var(--border)] overflow-hidden">
          <input
            type="text"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoeken..."
            className="w-full px-3 py-2 text-sm border-b border-[var(--border)] outline-none bg-transparent"
          />
          <div className="max-h-44 overflow-y-auto">
            {isLoading ? (
              <p className="px-3 py-2 text-sm text-[var(--muted-foreground)]">Laden...</p>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-[var(--muted-foreground)]">Geen resultaten</p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => { onSelect(o.id); setOpen(false); setSearch(''); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--muted)] transition-colors"
                >
                  {o.label}
                  {o.sub && <span className="text-[var(--muted-foreground)] ml-1">· {o.sub}</span>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function AddRenoTicketModal({ isOpen, onClose, onCreated }: Props) {
  const { session } = useSession();
  const { user: currentUser } = useUser();
  const { buildings, getUnitsForBuilding } = useBuildingsAndUnits();
  const { users: activeUsers } = useActiveUsers();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [buildingId, setBuildingId] = useState<string | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [renovationProjectId, setRenovationProjectId] = useState<number | null>(null);
  const [projectPartId, setProjectPartId] = useState<number | null>(null);
  const [messageType, setMessageType] = useState<RenoMessageType | ''>('');
  const [priority, setPriority] = useState<RenoTicketPriority>(RenoTicketPriority.UNKNOWN);
  const [source, setSource] = useState<RenoTicketSource>(RenoTicketSource.INTERNAL);

  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [reporter, setReporter] = useState<{ id: number; name: string } | null>(null);
  // Eenmalig per opening voorvullen: zo overschrijven we een bewuste wijziging/verwijdering
  // van de gebruiker niet wanneer de gebruikerslijst nadien nog verandert.
  const hasPrefilledAssignee = useRef(false);
  const [suppliers, setSuppliers] = useState<{ id: number; name: string }[]>([]);

  const toggleSupplier = (s: { id: number; name: string }) =>
    setSuppliers((prev) => (prev.some((x) => x.id === s.id) ? prev.filter((x) => x.id !== s.id) : [...prev, s]));

  const [projects, setProjects] = useState<RenovationProject[]>([]);
  const [parts, setParts] = useState<ProjectPart[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pickerMode, setPickerMode] = useState<'assignee' | 'reporter' | 'supplier' | null>(null);

  // Mail bij aanmaken: kanaal (RS/RT) + ontvangers.
  const [channel, setChannel] = useState<'supplier' | 'tenant'>('supplier');
  const [recipientsPreview, setRecipientsPreview] = useState<{
    contactPerson: { id: number; name: string; email: string } | null;
    unitTenants: { id: number; name: string; email: string }[];
    buildingTenants: { id: number; name: string; email: string }[];
    suppliers: { id: number; name: string; email: string }[];
  } | null>(null);
  const [selectedRecipientKeys, setSelectedRecipientKeys] = useState<string[]>([]);
  // Vrij toegevoegde ontvangers (contactpersoon of firma buiten ticket/project).
  const [extraRecipients, setExtraRecipients] = useState<AddedRecipient[]>([]);

  // De aanmaker van het ticket standaard als interne verantwoordelijke voorstellen.
  // Blijft aanpasbaar via de picker (incl. leegmaken).
  useEffect(() => {
    if (!isOpen) {
      hasPrefilledAssignee.current = false;
      return;
    }
    if (hasPrefilledAssignee.current || !currentUser) return;
    // Wachten tot de gebruikerslijst geladen is: enkel een gekende actieve gebruiker
    // voorvullen, anders zouden we een ruwe Clerk-id tonen.
    if (activeUsers.length === 0) return;
    hasPrefilledAssignee.current = true;
    if (activeUsers.some((u) => u.id === currentUser.id)) setAssignedTo(currentUser.id);
  }, [isOpen, currentUser, activeUsers]);

  // Projecten van het geselecteerde gebouw ophalen.
  useEffect(() => {
    if (!buildingId || !session) { setProjects([]); return; }
    setIsLoadingProjects(true);
    fetchWithAuth(`${API_URL}/renovation/project/building/${buildingId}`, { method: 'GET' }, session)
      .then((r) => r.json())
      .then((data) => setProjects(Array.isArray(data) ? data : data.projects ?? []))
      .catch(() => setProjects([]))
      .finally(() => setIsLoadingProjects(false));
  }, [buildingId, session]);

  // Onderdelen van het geselecteerde project ophalen (incl. label + betrokken leveranciers).
  useEffect(() => {
    if (!renovationProjectId || !session) { setParts([]); return; }
    fetchWithAuth(`${API_URL}/renovation/project/${renovationProjectId}/parts`, { method: 'GET' }, session)
      .then((r) => r.json())
      .then((data) => setParts(Array.isArray(data) ? data : []))
      .catch(() => setParts([]));
  }, [renovationProjectId, session]);

  const units = buildingId ? getUnitsForBuilding(buildingId) : [];

  // Leveranciers die al bij dit project betrokken zijn (suggestie, niet beperkend).
  const suggestedSuppliers = useMemo(() => {
    const map = new Map<number, string>();
    parts.forEach((p) =>
      (p.supplierAssignments ?? []).forEach((sa) => {
        if (sa.supplier) map.set(sa.supplier.id, sa.supplier.name);
      }),
    );
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [parts]);

  const selectedPart = parts.find((p) => p.id === projectPartId);

  // Ontvangers vooraf oplossen (e-mailadressen) op basis van de huidige selectie.
  const fetchRecipientsPreview = useCallback(async () => {
    if (!session || !buildingId) { setRecipientsPreview(null); return; }
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/recipients/preview`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            buildingId,
            unitId: unitId || undefined,
            supplierIds: suppliers.map((s) => s.id),
            reporterId: reporter?.id,
          }),
        },
        session,
      );
      if (res.ok) setRecipientsPreview(await res.json());
    } catch {
      /* preview is optioneel: vrij zoeken blijft werken */
    }
  }, [session, buildingId, unitId, suppliers, reporter]);

  useEffect(() => {
    const t = setTimeout(() => { fetchRecipientsPreview(); }, 250);
    return () => clearTimeout(t);
  }, [fetchRecipientsPreview]);

  // Ontvanger-opties, gefilterd op kanaal (zoals op de detailpagina).
  const recipientOptions = useMemo(() => {
    const opts: {
      key: string;
      label: string;
      sublabel?: string;
      recipients: { name: string; email: string }[];
      warning?: string;
      entity?: { type: 'contact' | 'company'; id: number };
    }[] = [];
    // Enkel ontvangers met een adres zijn aanmailbaar; de rest tonen we met een
    // waarschuwing i.p.v. ze te verbergen.
    const mailable = (list: { name: string; email: string }[]) =>
      list.filter((t) => t.email?.trim()).map((t) => ({ name: t.name, email: t.email.trim() }));

    if (recipientsPreview) {
      if (channel === 'tenant') {
        const buildingMailable = mailable(recipientsPreview.buildingTenants);
        const unitMailable = mailable(recipientsPreview.unitTenants);
        const buildingZonder = recipientsPreview.buildingTenants.length - buildingMailable.length;
        opts.push({
          key: 'building',
          label: 'Hele gebouw',
          sublabel: `${buildingMailable.length} huurder(s) met e-mailadres${buildingZonder > 0 ? ` — ${buildingZonder} zonder` : ''}`,
          recipients: buildingMailable,
          warning: buildingMailable.length === 0 ? 'Geen enkele huurder heeft een e-mailadres' : undefined,
        });
        opts.push({
          key: 'unit',
          label: 'Huurder van de unit',
          sublabel: unitMailable.map((t) => t.name).join(', ') || 'geen huurder gevonden',
          recipients: unitMailable,
          warning:
            unitMailable.length === 0 && recipientsPreview.unitTenants.length > 0
              ? 'Huurder van de unit heeft geen e-mailadres'
              : undefined,
        });
      }
      if (channel === 'supplier') {
        recipientsPreview.suppliers.forEach((s) => {
          const email = s.email?.trim();
          opts.push({
            key: `supplier-${s.id}`,
            label: s.name,
            sublabel: email || 'Leverancier',
            recipients: email ? [{ name: s.name, email }] : [],
            warning: email ? undefined : 'Geen e-mailadres bij deze leverancier',
            entity: { type: 'company', id: s.id },
          });
        });
      }
      if (recipientsPreview.contactPerson) {
        const contactEmail = recipientsPreview.contactPerson.email?.trim();
        opts.push({
          key: 'contact',
          label: 'Contactpersoon',
          sublabel: recipientsPreview.contactPerson.name,
          recipients: contactEmail
            ? [{ name: recipientsPreview.contactPerson.name, email: contactEmail }]
            : [],
          warning: contactEmail
            ? undefined
            : `${recipientsPreview.contactPerson.name} heeft geen e-mailadres`,
          entity: { type: 'contact', id: recipientsPreview.contactPerson.id },
        });
      }
    }

    // Vrij toegevoegde ontvangers staan onderaan, los van het kanaal.
    extraRecipients.forEach((extra) => {
      opts.push({
        key: `extra-${extra.email.toLowerCase()}`,
        label: extra.name,
        sublabel:
          extra.type === 'company'
            ? `Firma — ${extra.email}`
            : extra.type === 'manual'
              ? 'Los e-mailadres'
              : `Contactpersoon — ${extra.email}`,
        recipients: [{ name: extra.name, email: extra.email }],
      });
    });

    return opts;
  }, [recipientsPreview, channel, extraRecipients]);

  const handleAddExtraRecipient = useCallback((recipient: AddedRecipient) => {
    const email = recipient.email.trim();
    if (!email) return;
    const key = `extra-${email.toLowerCase()}`;
    setExtraRecipients((prev) =>
      prev.some((r) => r.email.toLowerCase() === email.toLowerCase())
        ? prev
        : [...prev, { ...recipient, email }],
    );
    setSelectedRecipientKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }, []);

  const selectedRecipients = useMemo(() => {
    const map = new Map<string, { name: string; email: string }>();
    for (const opt of recipientOptions) {
      if (!selectedRecipientKeys.includes(opt.key)) continue;
      for (const r of opt.recipients) if (r.email && !map.has(r.email)) map.set(r.email, r);
    }
    return Array.from(map.values());
  }, [recipientOptions, selectedRecipientKeys]);

  // Reden waarom er (nog) niet gemaild kan worden — expliciet in de UI i.p.v. enkel
  // een uitgeschakelde knop met een tooltip.
  const mailBlockReason = useMemo(
    () =>
      renoMailBlockReason({
        channel,
        hasBuilding: !!buildingId,
        options: recipientOptions,
        selectedCount: selectedRecipients.length,
      }),
    [channel, buildingId, recipientOptions, selectedRecipients.length],
  );

  // Selectie resetten bij kanaalwissel; vrij toegevoegde ontvangers blijven staan.
  useEffect(() => {
    setSelectedRecipientKeys((prev) => prev.filter((k) => k.startsWith('extra-')));
  }, [channel]);

  const toggleRecipientKey = (key: string) =>
    setSelectedRecipientKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const handleSubmit = async (sendMail = false) => {
    if (!title.trim() || !buildingId) return;
    setIsSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        buildingId,
        unitId: unitId || undefined,
        renovationProjectId: renovationProjectId || undefined,
        projectPartId: projectPartId || undefined,
        messageType: messageType || undefined,
        priority,
        source,
        assignedTo: assignedTo || undefined,
        reporterId: reporter?.id || undefined,
        supplierIds: suppliers.length ? suppliers.map((s) => s.id) : undefined,
      };
      await onCreated(payload, sendMail ? { channel, recipients: selectedRecipients } : undefined);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setBuildingId(null);
    setUnitId(null);
    setRenovationProjectId(null);
    setProjectPartId(null);
    setMessageType('');
    setPriority(RenoTicketPriority.UNKNOWN);
    setSource(RenoTicketSource.INTERNAL);
    setAssignedTo(null);
    setReporter(null);
    setSuppliers([]);
    setProjects([]);
    setParts([]);
    setChannel('supplier');
    setRecipientsPreview(null);
    setSelectedRecipientKeys([]);
    setExtraRecipients([]);
    onClose();
  };

  return (
    <>
      {/* modal=false zodra een picker open is: anders blokkeert Radix (pointer-events/focus-trap)
          de geportalde UniversalPickerModal waardoor je er niet mee kan interageren. */}
      <Dialog open={isOpen} onOpenChange={(o) => !o && handleClose()} modal={pickerMode === null}>
        <DialogContent
          className="max-w-lg max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => { if (pickerMode !== null) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Nieuw renovatieticket</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* 1. Gebouw (verplicht) + Unit (optioneel) */}
            <InlineSearchSelect
              label="Gebouw *"
              icon={<Buildings size={15} />}
              placeholder="Selecteer gebouw..."
              selectedLabel={buildingId}
              options={buildings.map((b) => ({
                id: b.id,
                label: b.id,
                sub: b.company?.name,
              }))}
              onSelect={(id) => { setBuildingId(id); setUnitId(null); setRenovationProjectId(null); setProjectPartId(null); }}
            />

            {buildingId && (
              <div className="space-y-1">
                <Label>Unit (optioneel)</Label>
                <Select value={unitId ?? 'none'} onValueChange={(v) => setUnitId(v === 'none' ? null : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Volledig gebouw" />
                  </SelectTrigger>
                  <SelectContent {...selectContentProps}>
                    <SelectItem value="none">Volledig gebouw</SelectItem>
                    {units.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        <span className="flex items-center gap-2"><Cube size={13} /> {u.id}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* 2. Renovatieproject (enkel van dit gebouw) */}
            {buildingId && (
              <InlineSearchSelect
                label="Renovatieproject"
                icon={<Wrench size={15} />}
                placeholder={isLoadingProjects ? 'Projecten laden...' : 'Selecteer project...'}
                selectedLabel={projects.find((p) => p.id === renovationProjectId)?.title}
                options={projects.map((p) => ({ id: String(p.id), label: p.title, sub: `RENO-${p.id}` }))}
                isLoading={isLoadingProjects}
                onSelect={(id) => { setRenovationProjectId(Number(id)); setProjectPartId(null); }}
              />
            )}

            {/* 3. Projectonderdeel (met label) */}
            {renovationProjectId && parts.length > 0 && (
              <div className="space-y-1">
                <Label>Projectonderdeel</Label>
                <Select
                  value={projectPartId ? String(projectPartId) : 'none'}
                  onValueChange={(v) => setProjectPartId(v === 'none' ? null : Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer onderdeel..." />
                  </SelectTrigger>
                  <SelectContent {...selectContentProps}>
                    <SelectItem value="none">Geen onderdeel</SelectItem>
                    {parts.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.title}{p.label?.name ? ` — ${p.label.name}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedPart?.label?.name && (
                  <p className="text-xs text-[var(--muted-foreground)]">Label: {selectedPart.label.name}</p>
                )}
              </div>
            )}

            {/* 4. Interne verantwoordelijke + Contactpersoon */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Interne verantwoordelijke</Label>
                <button
                  type="button"
                  onClick={() => setPickerMode('assignee')}
                  className="flex w-full items-center gap-2 rounded-md border border-[var(--border)] px-3 py-2 text-sm text-left hover:bg-[var(--muted)] transition-colors"
                >
                  <UserCircleGear size={15} />
                  <span className={assignedTo ? 'truncate' : 'text-[var(--muted-foreground)]'}>
                    {assignedTo
                      ? getUserName(assignedTo, activeUsers).name || 'Onbekende gebruiker'
                      : 'Bv. Melanie...'}
                  </span>
                </button>
              </div>
              <div className="space-y-1">
                <Label>Contactpersoon</Label>
                <button
                  type="button"
                  onClick={() => setPickerMode('reporter')}
                  className="flex w-full items-center gap-2 rounded-md border border-[var(--border)] px-3 py-2 text-sm text-left hover:bg-[var(--muted)] transition-colors"
                >
                  <User size={15} />
                  <span className={reporter ? 'truncate' : 'text-[var(--muted-foreground)]'}>
                    {reporter?.name || 'Selecteer contact...'}
                  </span>
                </button>
              </div>
            </div>

            {/* 5. Leveranciers (meerdere mogelijk; suggestie uit project, vrij te kiezen) */}
            <div className="space-y-1">
              <Label>Leveranciers</Label>
              {suppliers.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pb-1">
                  {suppliers.map((s) => (
                    <span key={s.id} className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--muted)] px-2 py-1 text-xs">
                      <Wrench size={12} />
                      {s.name}
                      <button type="button" onClick={() => toggleSupplier(s)} className="text-[var(--muted-foreground)] hover:text-red-600">
                        <X size={12} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => setPickerMode('supplier')}
                className="flex w-full items-center gap-2 rounded-md border border-dashed border-[var(--border)] px-3 py-2 text-sm text-left text-[var(--muted-foreground)] hover:bg-[var(--muted)] transition-colors"
              >
                <Plus size={15} />
                Leverancier toevoegen
              </button>
              {suggestedSuppliers.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <span className="text-xs text-[var(--muted-foreground)] w-full">Betrokken bij dit project:</span>
                  {suggestedSuppliers.map((s) => {
                    const isSelected = suppliers.some((x) => x.id === s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleSupplier(s)}
                        className={`rounded-full border px-2 py-0.5 text-xs transition-colors ${
                          isSelected
                            ? 'bg-moss-secondary-fine border-moss-primary text-moss-primary'
                            : 'border-[var(--border)] hover:bg-[var(--muted)]'
                        }`}
                      >
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 6. Titel + beschrijving */}
            <div className="space-y-1">
              <Label htmlFor="title">Titel *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Bv. Offerteaanvraag badkamermeubelen..."
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="desc">Beschrijving</Label>
              <Textarea
                id="desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Aanvullende details..."
                rows={3}
              />
            </div>

            {/* 7. Type boodschap / prioriteit / bron */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Type boodschap</Label>
                <Select value={messageType || 'none'} onValueChange={(v) => setMessageType(v === 'none' ? '' : (v as RenoMessageType))}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent {...selectContentProps}>
                    <SelectItem value="none">—</SelectItem>
                    {Object.values(RenoMessageType).map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Prioriteit</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as RenoTicketPriority)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent {...selectContentProps}>
                    {Object.values(RenoTicketPriority).map((p) => (
                      <SelectItem key={p} value={p}>{p === 'Unknown' ? 'Onbekend' : p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Bron</Label>
                <Select value={source} onValueChange={(v) => setSource(v as RenoTicketSource)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent {...selectContentProps}>
                    {Object.values(RenoTicketSource).map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* 8. Mail meesturen bij aanmaken (optioneel) */}
            <div className="space-y-2 rounded-md border border-[var(--border)] p-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5">
                  <EnvelopeSimple size={15} /> Mail versturen bij aanmaken
                </Label>
                <div className="flex items-center gap-2 rounded-md border border-[var(--border)] px-2.5 py-1">
                  <span className={`text-xs ${channel === 'tenant' ? 'font-semibold text-moss-primary' : 'text-[var(--muted-foreground)]'}`}>Huurder</span>
                  <Switch checked={channel === 'supplier'} onCheckedChange={(c) => setChannel(c ? 'supplier' : 'tenant')} />
                  <span className={`text-xs ${channel === 'supplier' ? 'font-semibold text-moss-primary' : 'text-[var(--muted-foreground)]'}`}>Leverancier</span>
                </div>
              </div>
              <p className="text-xs text-[var(--muted-foreground)]">
                Kies ontvangers. Na het aanmaken open je de mail om te schrijven en te versturen
                (code {channel === 'tenant' ? 'RT' : 'RS'}).
              </p>
              {/* Waarom er nu niet gemaild kan worden + de regels erachter. Zonder dit
                  stond de verzendknop enkel uit, met de reden in een tooltip. */}
              <RenoMailInfoNotice blockReason={mailBlockReason} />
              {/* Vrij zoeken: ook contacten/firma's die niet aan ticket of project hangen. */}
              <RenoRecipientSearch
                onAdd={handleAddExtraRecipient}
                existingEmails={recipientOptions.flatMap((o) => o.recipients.map((r) => r.email))}
              />
              {!buildingId ? (
                <p className="text-xs text-[var(--muted-foreground)]">Selecteer eerst een gebouw.</p>
              ) : recipientOptions.length === 0 ? (
                <p className="text-xs text-[var(--muted-foreground)]">
                  {channel === 'supplier'
                    ? 'Voeg leveranciers toe of zoek hierboven een contactpersoon of firma.'
                    : 'Geen huurders/contact gevonden — zoek hierboven een ontvanger.'}
                </p>
              ) : (
                <div className="space-y-1">
                  {recipientOptions.map((opt) => {
                    const disabled = opt.recipients.length === 0;

                    // Ontbrekend adres bij een gekende fiche: hier meteen aanvullen.
                    if (disabled && opt.entity) {
                      return (
                        <div key={opt.key} className="flex items-start gap-2 rounded bg-amber-50/60 px-1 py-1">
                          <Warning size={13} weight="fill" className="mt-1 shrink-0 text-amber-700" />
                          <div className="min-w-0 flex-1 space-y-1">
                            <span className="block truncate text-sm text-amber-900">{opt.label}</span>
                            <span className="block truncate text-[10px] text-amber-800">
                              {opt.warning ?? 'Geen e-mailadres bekend'}
                            </span>
                            <RenoAddEmailInline
                              compact
                              type={opt.entity.type}
                              id={opt.entity.id}
                              name={opt.label}
                              onSaved={() => {
                                setSelectedRecipientKeys((prev) =>
                                  prev.includes(opt.key) ? prev : [...prev, opt.key],
                                );
                                fetchRecipientsPreview();
                              }}
                            />
                          </div>
                        </div>
                      );
                    }

                    return (
                      <label
                        key={opt.key}
                        title={disabled ? opt.warning ?? 'Geen e-mailadres bekend' : undefined}
                        className={`flex items-center gap-2 rounded px-1 py-1 text-sm ${
                          disabled ? 'cursor-not-allowed bg-amber-50/60' : 'cursor-pointer hover:bg-[var(--muted)]'
                        }`}
                      >
                        <Checkbox
                          checked={selectedRecipientKeys.includes(opt.key)}
                          disabled={disabled}
                          onCheckedChange={() => toggleRecipientKey(opt.key)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className={`block truncate ${disabled ? 'text-amber-900' : ''}`}>{opt.label}</span>
                          {/* Ontbrekend adres primeert op het gewone sublabel. */}
                          {disabled ? (
                            <span className="flex items-center gap-1 truncate text-[10px] text-amber-800">
                              <Warning size={11} weight="fill" className="shrink-0 text-amber-700" />
                              {opt.warning ?? 'Geen e-mailadres bekend'}
                            </span>
                          ) : (
                            opt.sublabel && (
                              <span className="block truncate text-[10px] text-[var(--muted-foreground)]">{opt.sublabel}</span>
                            )
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>Annuleren</Button>
            <Button variant="outline" onClick={() => handleSubmit(false)} disabled={!title.trim() || !buildingId || isSubmitting}>
              Aanmaken
            </Button>
            <Button
              onClick={() => handleSubmit(true)}
              disabled={!title.trim() || !buildingId || isSubmitting || selectedRecipients.length === 0}
              title={mailBlockReason ?? 'Ticket aanmaken en mail opstellen'}
            >
              <EnvelopeSimple size={14} className="mr-1" />
              Aanmaken &amp; mail versturen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UniversalPickerModal
        isOpen={pickerMode === 'assignee'}
        mode="assignee"
        title="Interne verantwoordelijke selecteren"
        onClose={() => setPickerMode(null)}
        onConfirm={(user: any) => { setAssignedTo(user?.id ?? null); setPickerMode(null); }}
        config={{ allowRemoval: true }}
      />

      <UniversalPickerModal
        isOpen={pickerMode === 'reporter'}
        mode="reporter"
        title="Contactpersoon selecteren"
        onClose={() => setPickerMode(null)}
        onConfirm={(contact: any) => {
          setReporter(
            contact
              ? { id: contact.id, name: `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() || contact.email || `#${contact.id}` }
              : null,
          );
          setPickerMode(null);
        }}
        config={{ allowRemoval: true }}
      />

      <UniversalPickerModal
        isOpen={pickerMode === 'supplier'}
        mode="supplier"
        title="Leverancier selecteren"
        onClose={() => setPickerMode(null)}
        onConfirm={(s: any) => { if (s?.id) toggleSupplier({ id: s.id, name: s.name }); setPickerMode(null); }}
        // allowCreateSupplier: staat de leverancier nog niet in YallyHub, dan kan hij hier
        // meteen aangemaakt worden en wordt hij direct aan het ticket gehangen.
        config={{ allowRemoval: true, allowCreateSupplier: true }}
      />
    </>
  );
}
